import { ChevronDown, Database, Info } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getTypeColor, typeSwatchStyle } from '../dashboard/taskTypeMeta';

export type DoneByTypeRow = { type: string; count: number };

interface Props {
  totalDoneIssues: number;
  byType: DoneByTypeRow[];
  periodActive: boolean;
}

export const DoneIssuesStatCard: React.FC<Props> = ({ totalDoneIssues, byType, periodActive }) => {
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const rows = byType.filter((r) => r.count > 0);
  const hint = periodActive
    ? 'Distinct issues in status Done that have worklog in the selected Period. Click the card for a breakdown by type.'
    : 'Distinct issues in status Done that have worklog rows for this product (all-time). Click the card for a breakdown by type.';

  const onCardActivate = (e: React.MouseEvent | React.KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('.stat-hint-icon')) return;
    if (target?.closest('.done-by-type-popover')) return;
    if ('key' in e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
    }
    toggle();
  };

  return (
    <div
      ref={cardRef}
      className={`stat-card-item stat-card-item--expandable${open ? ' is-open' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-controls="done-by-type-popover"
      onClick={onCardActivate}
      onKeyDown={onCardActivate}
    >
      <div className="stat-icon stat-icon-purple">
        <Database className="w-6 h-6" />
      </div>
      <div className="stat-details">
        <span className="stat-label">
          Total Issues Done
          <span className="stat-hint-icon" title={hint} aria-label="Hint">
            <Info size={14} />
          </span>
        </span>
        <div className="stat-value-row">
          <span className="stat-value">{totalDoneIssues}</span>
          <ChevronDown className="stat-chevron" aria-hidden />
        </div>
      </div>
      <div
        className="done-by-type-popover"
        id="done-by-type-popover"
        hidden={!open}
        role="dialog"
        aria-label="Done issues by type"
      >
        <h4>Done issues by type</h4>
        {rows.length === 0 ? (
          <div className="done-by-type-row">
            <span className="name">No Done issues in this scope</span>
          </div>
        ) : (
          rows.map((r, i) => {
            const pct = totalDoneIssues > 0 ? (r.count / totalDoneIssues) * 100 : 0;
            const color = getTypeColor(r.type, i);
            return (
              <div key={r.type} className="done-by-type-row">
                <span className="swatch" style={typeSwatchStyle(r.type, color, 12)} />
                <span className="name">{r.type}</span>
                <span className="count">{r.count.toLocaleString('en-US')}</span>
                <span className="pct">{pct.toFixed(1)}%</span>
              </div>
            );
          })
        )}
        <div className="done-by-type-footer">
          <span>Total</span>
          <span>{totalDoneIssues.toLocaleString('en-US')}</span>
        </div>
      </div>
    </div>
  );
};
